import {
  DATA_SOURCE_TYPE_API_MAP,
  DATA_SOURCE_TYPES,
  listViewGroupingIntervalMap
} from './../../../constants'
import { CompanyWideActivityWidgetParams, DashboardListParams, DashboardCategory } from './slice'
import { getAfterCursor, parameterizeArrayofObjects } from '../../../utils/graphqlUtil'
import getWidgetMap from '../renderer/utils/widgetMap'
import { DashboardData } from '../renderer/types/interfaces'
import { gql } from 'graphql-request'
import { IntlShape } from 'react-intl'

export const queryDashboardList = (params?: DashboardListParams) => {
  const { page, pageSize, sortBy, sortOrder, searchQuery, owner, compliance } = params || {}

  let paginationParams = ''

  if (page && pageSize) {
    const cursor = getAfterCursor(page, pageSize)
    paginationParams = `first: ${pageSize}, after: "${cursor}"`
  }

  const filter: { key: string; values: string[] }[] = []
  if (owner && owner.length > 0) {
    filter.push({ key: 'OWNER', values: owner })
  }
  if (compliance && compliance.length > 0) {
    filter.push({ key: 'COMPLIANCE', values: compliance })
  }

  return gql`
    {
      dashboards(sortField: ${sortBy || 'NAME'}, sortByAsc: ${sortOrder == 1}, query: "${
    searchQuery?.toLowerCase() || ''
  }", ${
    filter.length > 0 ? `filter: ${parameterizeArrayofObjects(filter)}` : ''
  } ${paginationParams}) {
        count
        edges {
          node {
            id
            dashboardName
            dashboardType
            datasourceId
            datasourceType
            createdBy
            compliance
            editors
            userPreference {
              edges {
                node {
                  default
                  pinned
                }
              }
            }
          }
        }
      }
    }
  `
}

export const mapDashboardList = (rawResult): { list: DashboardData[]; total: number } => {
  const dashboardList: DashboardData[] = rawResult?.dashboards?.edges?.map((edge) => {
    return {
      id: edge.node?.id,
      name: edge.node?.dashboardName,
      datasourceId: edge.node?.datasourceId,
      datasourceType: edge.node?.datasourceType,
      createdBy: edge.node?.createdBy,
      compliance: edge.node?.compliance,
      editors: edge.node?.editors,
      isDefault: edge.node?.userPreference?.edges[0]?.node?.default || false,
      isPinned: edge.node?.userPreference?.edges[0]?.node?.pinned || false,
      type: edge.node?.dashboardType
    }
  })

  return { list: dashboardList, total: rawResult?.dashboards?.count }
}

const mapDashboardListForUpdateDashboardPreferences = (dashboardList: DashboardData[]) => {
  return dashboardList
    .map((dashboard) => {
      const dashboardId = `dashboardId: "${dashboard.id}",`
      const datasourceId = dashboard.datasourceId
        ? `datasourceId: "${dashboard.datasourceId}",`
        : ''
      const defaultField = `default: ${dashboard.isPinned ? dashboard.isDefault || false : false},`
      const pinnedField = `pinned: ${dashboard.isPinned}`

      return `{ ${dashboardId} ${datasourceId} ${defaultField} ${pinnedField} }`
    })
    .join(', ')
}

export const mutationSaveUserDashboardPreferences = (dashboardList: DashboardData[]) => {
  return gql`
    mutation {
      updateUserDashboardsPreference(
        clientMutationId: "updateUserDashboardsPreference"
        dashboards: [${mapDashboardListForUpdateDashboardPreferences(dashboardList)}]
      ) {
        clientMutationId
      }
    }
  `
}

export const queryDashboardListByDatasourceId = (
  datasourceId: string,
  datasourceType: string
) => gql`
  {
    dashboards(filter: [{ key: DATASOURCE_TYPE, values: ["${DATA_SOURCE_TYPE_API_MAP[datasourceType]}"] },{ key: DATASOURCE_ID, values: ["${datasourceId}"] }]) {
      count
      edges {
        node {
          id
          dashboardName
          dashboardType
          datasourceId
          datasourceType
          active
          screenWidthConfig {
            screenWidth
            cols
          }
          widgets {
            id
            widgetType
            gridArea {
              rowSpan
              colSpan
            }
          }
          userPreference {
            edges {
              node {
                default
                pinned
              }
            }
          }
        }
      }
    }
  }
`

export const mapQueryDashboardListByDatasourceId = (
  rawResult
): { dashboardList: DashboardData[]; selectedMainDashboard: string } => {
  let selectedMainDashboard = ''
  const dashboardList: DashboardData[] = rawResult?.dashboards?.edges?.map((edge) => {
    if (edge.node?.userPreference?.edges[0]?.node?.default) {
      selectedMainDashboard = edge.node?.id
    }
    return {
      id: edge.node?.id,
      name: edge.node?.dashboardName,
      isDefault: edge.node?.userPreference?.edges[0]?.node?.default || false,
      isPinned: edge.node?.userPreference?.edges[0]?.node?.pinned || false,
      type: edge.node?.dashboardType,
      isActive: edge.node?.active || false,
      screenWidthConfig: edge.node?.screenWidthConfig,
      widgets: edge.node?.widgets,
      datasourceType: edge.node?.datasourceType,
      datasourceId: edge.node?.datasourceId
    }
  })

  return { dashboardList, selectedMainDashboard }
}

export const queryDashboardById = (params: {
  dashboardId: string
  dataSourceId?: string
  datasourceType?: DATA_SOURCE_TYPES
}) => {
  const { dashboardId, dataSourceId, datasourceType } = params
  const hasDatasourceType = datasourceType && DATA_SOURCE_TYPE_API_MAP[datasourceType]
  const hasDataSourceId = dataSourceId

  let filterString = ''
  if (hasDatasourceType && hasDataSourceId) {
    filterString = `, filter: [
      { key: DATASOURCE_TYPE, values: ["${DATA_SOURCE_TYPE_API_MAP[datasourceType!]}"] },
      { key: DATASOURCE_ID, values: ["${dataSourceId}"] }
    ]`
  } else if (hasDatasourceType) {
    filterString = `, filter: [
      { key: DATASOURCE_TYPE, values: ["${DATA_SOURCE_TYPE_API_MAP[datasourceType!]}"] }
    ]`
  } else if (hasDataSourceId) {
    filterString = `, filter: [
      { key: DATASOURCE_ID, values: ["${dataSourceId}"] }
    ]`
  }

  const dashboardIdParamString =
    hasDataSourceId || hasDatasourceType ? '' : `dashboardId: "${dashboardId}"`
  return gql`
  {
    dashboards(${dashboardIdParamString}${filterString}) {
      edges {
        node {
          id
          dashboardName
          dashboardType
          datasourceId
          datasourceType
          active
          compliance
          screenWidthConfig {
            screenWidth
            cols
          }
          widgets {
            id
            widgetType
            datasourceId
            datasourceName
            datasourceType
            gridArea {
              rowSpan
              colSpan
            }
          }
          userPreference {
            edges {
              node {
                default
                pinned
              }
            }
          }
        }
      }
    }
  }
`
}

export const mapQueryDashboardById = (raw: any) => {
  const dashboardData = raw?.dashboards?.edges[0]?.node

  return {
    id: dashboardData?.id,
    name: dashboardData?.dashboardName,
    type: dashboardData?.dashboardType,
    active: dashboardData?.active,
    screenWidthConfig: dashboardData?.screenWidthConfig,
    compliance: dashboardData?.compliance,
    widgets: dashboardData?.widgets,
    datasourceType: dashboardData?.datasourceType,
    datasourceId: dashboardData?.datasourceId,
    isDefault: dashboardData?.userPreference?.edges[0]?.node?.default || false,
    isPinned: dashboardData?.userPreference?.edges[0]?.node?.pinned || false
  }
}

export const queryDashboardByType = (dashboardType: string) => gql`
  {
    dashboards(filter: [{key: DASHBOARD_TYPE, values: "${dashboardType}"}]) {
      edges {
        node {
          id
          dashboardName
          dashboardType
          active
          compliance
          screenWidthConfig {
            screenWidth
            cols
          }
          widgets {
            id
            widgetType
            gridArea {
              rowSpan
              colSpan
            }
          }
        }
      }
    }
  }
`

export const mapQueryDashboardByType = (raw: any) => {
  const dashboardData = raw?.dashboards?.edges[0]?.node

  return {
    id: dashboardData?.id,
    name: dashboardData?.dashboardName,
    type: dashboardData?.dashboardType,
    active: dashboardData?.active,
    screenWidthConfig: dashboardData?.screenWidthConfig,
    compliance: dashboardData?.compliance,
    widgets: dashboardData?.widgets
  }
}

export const mapQueryEditDashboardById = (raw: any, intl: IntlShape): DashboardData | null => {
  const dashboardData = raw.dashboards.edges[0].node

  if (!dashboardData) return null

  const widgetMap = getWidgetMap(intl)

  const formattedWidgetList = dashboardData?.widgets?.map((widget) => ({
    id: widget?.id,
    widgetType: widget?.widgetType,
    gridArea: widget?.gridArea,
    datasourceId: widget?.datasourceId,
    datasourceName: widget?.datasourceName,
    datasourceType: widget?.datasourceType,
    isSelected: true,
    widgetName: widgetMap[widget?.widgetType]?.widgetName || 'Unknown Widget'
  }))

  return dashboardData
    ? {
        id: dashboardData?.id,
        name: dashboardData?.dashboardName,
        type: dashboardData?.dashboardType,
        active: dashboardData?.active,
        screenWidthConfig: dashboardData?.screenWidthConfig,
        datasourceId: dashboardData?.datasourceId,
        datasourceType: dashboardData?.datasourceType,
        widgets: formattedWidgetList,
        compliance: dashboardData?.compliance
      }
    : null
}

export const mutationDeleteDashboard = (dashboardId?: string) => gql`
    mutation{
      deleteDashboard(
        clientMutationId: "deleteDashboard"
        dashboardId: "${dashboardId}"
      ) {
        deletedDashboardId
      }
    }`

export const queryUpdateDashboard = (dashboard: DashboardData) => `
  mutation {
    updateDashboard(
      clientMutationId: "updateDashboard"
      dashboardId: "${dashboard.id}"
      dashboardData: {
        dashboardName: "${dashboard.name}",
        dashboardType: "${dashboard.type}",
         ${
           dashboard.type === DashboardCategory.datasource_summary_dashboard
             ? `datasourceId: "${dashboard.datasourceId}",\n datasourceType: "${dashboard.datasourceType}",\n`
             : ''
         }
        screenWidthConfig: [
          ${dashboard.screenWidthConfig.map(
            (sc) => `{ screenWidth: ${sc.screenWidth}, cols: ${sc.cols} }`
          )}
        ],
        ${
          dashboard?.compliance && dashboard?.compliance.length > 0
            ? `compliance: ${JSON.stringify(dashboard.compliance)}`
            : ''
        },
        widgets: [
          ${dashboard.widgets.map(
            (widget) => `{
              id: "${widget.id}",
              widgetType: "${widget.widgetType}",
              ${widget.datasourceId ? `datasourceId: "${widget.datasourceId}"` : ''}
              ${widget.datasourceName ? `datasourceName: "${widget.datasourceName}"` : ''}
              ${widget.datasourceType ? `datasourceType: "${widget.datasourceType}"` : ''}
              gridArea: {
                rowSpan: ${widget.gridArea.rowSpan},
                colSpan: ${widget.gridArea.colSpan}
              },
          }`
          )}
        ]
      }
    ) {
      dashboardId
      clientMutationId
    }
  }
`

export const queryCreateDashboard = (dashboard: DashboardData) => `
  mutation {
    createDashboard(
      clientMutationId: "createDashboard"
      ${dashboard.id ? `dashboardId: "${dashboard.id}"` : ''}
      dashboardData: {
        dashboardName: "${dashboard.name}",
        dashboardType: "${dashboard.type}",
        screenWidthConfig: [
          ${dashboard.screenWidthConfig.map(
            (sc) => `{ screenWidth: ${sc.screenWidth}, cols: ${sc.cols} }`
          )}
        ],
        ${
          dashboard?.compliance && dashboard?.compliance.length > 0
            ? `compliance: ${JSON.stringify(dashboard.compliance)}`
            : ''
        },
        widgets: [
          ${dashboard.widgets.map(
            (widget) => `{
              id: "${widget.id}",
              widgetType: "${widget.widgetType}",
              ${widget.datasourceId ? `datasourceId: "${widget.datasourceId}"` : ''}
              ${widget.datasourceName ? `datasourceName: "${widget.datasourceName}"` : ''}
              ${widget.datasourceType ? `datasourceType: "${widget.datasourceType}"` : ''}
              gridArea: {
                rowSpan: ${widget.gridArea.rowSpan},
                colSpan: ${widget.gridArea.colSpan}
              },
          }`
          )}
        ]
      }
    ) {
      dashboardId
      clientMutationId
    }
  }
`

export const queryCompanyWideActivityWidget = (params: CompanyWideActivityWidgetParams) => {
  const {
    datasourceId,
    actualTimeRange,
    predictedTimeRange,
    groupingInterval,
    timezone,
    features,
    isSensitive
  } = params

  return gql`
    query identityActivitySummary {
      identityActivitySummary(
        datasourceId: "${datasourceId}"
        actualTimeRange: {
          start: "${actualTimeRange.start}"
          end: "${actualTimeRange.end}"
        }
        ${
          predictedTimeRange
            ? `
            predictedTimeRange: {
              start: "${predictedTimeRange.start}"
              end: "${predictedTimeRange.end}"
            }`
            : ''
        }
        groupingInterval: ${listViewGroupingIntervalMap[groupingInterval]},
        ${timezone ? `timezone: "${timezone}"` : ''}
        features: [${features?.map((f) => f).join('\n')}]
      ) {
        startTimestamp
        endTimestamp
        summaries {
          feature
          ${
            isSensitive
              ? 'actualSensitiveActivityCount predictedSensitiveActivityCount'
              : 'actualActivityCount predictedActivityCount'
          }
        }
      }
    }
  `
}
export type FeatureActivitySummary = {
  feature: string
  userCount: number
  actualActivityCount: number
  actualSensitiveActivityCount: number
  predictedActivityCount?: number
  predictedSensitiveActivityCount?: number
}

export type IdentityActiveSummary = {
  startTimestamp?: string
  endTimestamp?: string
  totalUserCount?: number
  summaries?: FeatureActivitySummary[]
}

export type CompanyWideActivityWidgetResponse = {
  identityActivitySummary: IdentityActiveSummary[]
}

export const mapQueryCompanyWideActivityWidget = (
  raw: CompanyWideActivityWidgetResponse
): { list: IdentityActiveSummary[] } => {
  if (!raw?.identityActivitySummary) {
    return { list: [] }
  }
  return {
    list: raw.identityActivitySummary
  }
}
